import styled from "styled-components";
import Link from "next/link";

// Page Wrapper
export const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
`;

// Hero Section
export const HeroSection = styled.section`
  position: relative;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(
      135deg,
      rgba(200, 85, 61, 0.95) 0%,
      rgba(242, 133, 59, 0.55) 100%
    ),
    url("/images/home-header-lg.jpg") center/cover no-repeat;
  color: white;
  padding: 8rem 2rem 4rem;
  margin-top: 0;

  @media (max-width: 768px) {
    min-height: 75vh;
    padding: 5rem 1rem 2rem;
  }

  @media (max-width: 480px) {
    min-height: 70vh;
    padding: 4rem 1rem 2rem;
  }
`;

export const HeroContent = styled.div`
  max-width: 1400px;
  width: 100%;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    text-align: center;
    gap: 3rem;
  }
`;

export const HeroText = styled.div`
  z-index: 2;
`;

export const HeroTitle = styled.h1`
  font-size: 4rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
  line-height: 1.1;
  font-family: var(--font-poppins), sans-serif;

  @media (max-width: 1200px) {
    font-size: 3rem;
  }

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

export const HeroSubtitle = styled.p`
  font-size: 1.5rem;
  margin-bottom: 2rem;
  opacity: 0.95;
  line-height: 1.6;

  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

export const HeroButtons = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;

  @media (max-width: 968px) {
    justify-content: center;
  }

  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

export const HeroButton = styled(Link)<{ $variant?: "primary" | "secondary" }>`
  padding: 1.25rem 2.5rem;
  font-size: 1.2rem;
  font-weight: 700;
  border-radius: 12px;
  text-decoration: none;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;

  ${(props) =>
    props.$variant === "secondary"
      ? `
    background: transparent;
    color: white;
    border: 3px solid white;

    &:hover {
      background: white;
      color: #C8553D;
      transform: translateY(-3px);
      box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
    }
  `
      : `
    background: white;
    color: #C8553D;
    border: 3px solid white;

    &:hover {
      background: #f8f9fa;
      transform: translateY(-3px);
      box-shadow: 0 8px 20px rgba(0, 0, 0, 0.3);
    }
  `}

  @media (max-width: 768px) {
    padding: 1rem 2rem;
    font-size: 1.1rem;
    width: 100%;
    justify-content: center;
  }
`;

export const HeroImageContainer = styled.div`
  position: relative;
  z-index: 1;

  @media (max-width: 968px) {
    display: none;
  }
`;

export const HeroImage = styled.div`
  position: relative;
  width: 100%;
  height: 500px;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  background: url("/images/home-header-lg.jpg") center/cover no-repeat;

  img {
    object-fit: cover;
  }
`;

// Features Section
export const FeaturesSection = styled.section`
  padding: 6rem 2rem;
  background: white;

  @media (max-width: 768px) {
    padding: 4rem 1rem;
  }
`;

export const SectionTitle = styled.h2`
  font-size: 3rem;
  font-weight: 800;
  text-align: center;
  color: #2c3e50;
  margin-bottom: 1rem;
  font-family: var(--font-poppins), sans-serif;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

export const SectionSubtitle = styled.p`
  font-size: 1.3rem;
  text-align: center;
  color: #34495e;
  margin-bottom: 4rem;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;

  @media (max-width: 768px) {
    font-size: 1.1rem;
    margin-bottom: 3rem;
  }
`;

export const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2.5rem;
  max-width: 1400px;
  margin: 0 auto;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

export const FeatureCard = styled.div`
  padding: 2rem;
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  text-align: center;

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.12);
  }

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
  }
`;

export const FeatureIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 768px) {
    margin-bottom: 0;
    font-size: 2.5rem;
  }
`;

export const FeatureHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    flex-direction: row;
    justify-content: center;
    gap: 0.75rem;
    margin-bottom: 0.75rem;
  }
`;

export const FeatureTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 1rem;
  font-family: var(--font-poppins), sans-serif;

  @media (max-width: 768px) {
    font-size: 1.2rem;
    margin-bottom: 0;
  }
`;

export const FeatureDescription = styled.p`
  font-size: 1.1rem;
  color: #34495e;
  line-height: 1.6;
`;

export const CalloutSection = styled.section<{
  $bgColor?: string;
  $reverse?: boolean;
}>`
  padding: 6rem 2rem;
  background: ${(props) => props.$bgColor || "#f8f9fa"};

  @media (max-width: 768px) {
    padding: 4rem 1rem;
  }
`;

export const CalloutContent = styled.div<{ $reverse?: boolean }>`
  max-width: 1400px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;

  ${(props) =>
    props.$reverse &&
    `
    direction: rtl;
    > * {
      direction: ltr;
    }
  `}

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    direction: ltr;
    gap: 3rem;
  }
`;

export const CalloutImage = styled.div`
  position: relative;
  width: 100%;
  height: 400px;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);

  img {
    object-fit: cover;
  }

  @media (max-width: 768px) {
    height: 300px;
  }
`;

export const CalloutText = styled.div``;

export const CalloutTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 800;
  color: #2c3e50;
  margin-bottom: 1.5rem;
  font-family: var(--font-poppins), sans-serif;

  @media (max-width: 768px) {
    font-size: 1.5rem;
    margin-bottom: 1rem;
  }
`;

export const CalloutDescription = styled.p`
  font-size: 1.2rem;
  color: #34495e;
  line-height: 1.8;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

export const CalloutList = styled.ul`
  list-style: none;
  padding: 0;
  margin-bottom: 2rem;
`;

export const CalloutListItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1rem;
  font-size: 1.1rem;
  color: #34495e;

  &:before {
    content: "✓";
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    background: #c8553d;
    color: white;
    border-radius: 50%;
    flex-shrink: 0;
    font-weight: 700;
  }
`;

export const CalloutButton = styled(Link)`
  display: inline-block;
  padding: 1rem 2rem;
  background: #c8553d;
  color: white;
  text-decoration: none;
  font-weight: 700;
  font-size: 1.1rem;
  border-radius: 8px;
  transition: all 0.3s ease;

  &:hover {
    background: #a84532;
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(200, 85, 61, 0.3);
  }
`;

// CTA Section
export const CTASection = styled.section`
  padding: 6rem 2rem;
  background: linear-gradient(135deg, #558b8d 0%, #3a6264 100%);
  color: white;
  text-align: center;

  @media (max-width: 768px) {
    padding: 4rem 1rem;
  }
`;

export const CTAContent = styled.div`
  max-width: 900px;
  margin: 0 auto;
`;

export const CTATitle = styled.h2`
  font-size: 3rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
  font-family: var(--font-poppins), sans-serif;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

export const CTASubtitle = styled.p`
  font-size: 1.3rem;
  margin-bottom: 3rem;
  opacity: 0.95;

  @media (max-width: 768px) {
    font-size: 1.1rem;
    margin-bottom: 2rem;
  }
`;

export const CTAButtons = styled.div`
  display: flex;
  justify-content: center;
  gap: 1.5rem;
  flex-wrap: wrap;
`;

export const CTAButton = styled(Link)<{ $variant?: "primary" | "secondary" }>`
  padding: 1.25rem 2.5rem;
  font-size: 1.2rem;
  font-weight: 700;
  border-radius: 12px;
  text-decoration: none;
  transition: all 0.3s ease;

  ${(props) =>
    props.$variant === "secondary"
      ? `
    background: transparent;
    color: white;
    border: 3px solid white;

    &:hover {
      background: white;
      color: #558B8D;
      transform: translateY(-3px);
    }
  `
      : `
    background: white;
    color: #558B8D;
    border: 3px solid white;

    &:hover {
      background: #f8f9fa;
      transform: translateY(-3px);
      box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
    }
  `}

  @media (max-width: 480px) {
    width: 100%;
    text-align: center;
  }
`;
